const SERVICE_WORKER_PATH = "/push-sw.js";

export interface BrowserNotificationPayload {
  body: string;
  tag?: string;
  url?: string | null;
  requireInteraction?: boolean;
}

export function supportsBrowserNotifications() {
  return typeof window !== "undefined" && "Notification" in window;
}

export function supportsServiceWorkerNotifications() {
  return supportsBrowserNotifications()
    && typeof navigator !== "undefined"
    && "serviceWorker" in navigator;
}

export async function registerPushServiceWorker() {
  if (!supportsServiceWorkerNotifications()) {
    return null;
  }

  try {
    const existingRegistration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH);
    if (existingRegistration) {
      return existingRegistration;
    }

    return await navigator.serviceWorker.register(SERVICE_WORKER_PATH);
  } catch {
    return null;
  }
}

export async function requestBrowserNotificationPermission() {
  if (!supportsBrowserNotifications()) {
    return "denied" as NotificationPermission;
  }

  if (Notification.permission === "granted") {
    return "granted" as NotificationPermission;
  }

  if (Notification.permission === "denied") {
    return "denied" as NotificationPermission;
  }

  return Notification.requestPermission();
}

export async function showBrowserNotification(
  title: string,
  payload: BrowserNotificationPayload,
) {
  if (!supportsBrowserNotifications() || Notification.permission !== "granted") {
    return false;
  }

  const notificationUrl = payload.url
    ? new URL(payload.url, window.location.origin).toString()
    : new URL("/notifications", window.location.origin).toString();

  if (supportsServiceWorkerNotifications()) {
    const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH)
      || await registerPushServiceWorker();

    if (registration) {
      await registration.showNotification(title, {
        body: payload.body,
        tag: payload.tag,
        icon: "/favicon.ico",
        badge: "/favicon.ico",
        requireInteraction: payload.requireInteraction,
        data: { url: notificationUrl },
      });
      return true;
    }
  }

  const notification = new Notification(title, {
    body: payload.body,
    tag: payload.tag,
    icon: "/favicon.ico",
    requireInteraction: payload.requireInteraction,
  });

  notification.onclick = () => {
    window.focus();
    window.location.assign(notificationUrl);
    notification.close();
  };

  return true;
}
